import { useEffect, useState } from "react";

import {
  ITmpWardRule,
  ITmpWardStaffOption,
} from "../../../@types/admin/AdminTmpIntegration";
import {
  useAddTmpWardRule,
  useUpdateTmpWardRule,
} from "../../../hooks/mutations/admin/tmpIntegration";
import { useAllWardOptions } from "../../../hooks/queries/admin/selectValues";
import { useTmpWardStaff } from "../../../hooks/queries/admin/tmpIntegration";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage, HttpError } from "../../../utils/HttpError";
import {
  AdminSelectField,
  CheckboxField,
  FieldError,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
} from "../shared";

interface ITmpWardRuleModalProps {
  /** Omitted for a create. */
  rule?: ITmpWardRule;
  onClose: () => void;
}

/**
 * Create/edit a ward rule (EN-5203). A rule keys on the responsible HCP's
 * RIZIV number (`responsibleHcp.healthProviderId`, always on the order),
 * optionally narrowed by service — a matching-service rule wins over one
 * with no service. TMP may omit the prescriber id, so it is informational.
 * An order with no matching active rule sits in PendingMapping until one is
 * added here. The API refuses a duplicate (hcpHealthProviderId, serviceId)
 * pair with a 400 on `HcpHealthProviderId`, which lands on that field.
 */
/** "{first} {last} ({email})", degrading gracefully when parts are missing. */
export function staffOptionLabel(option: ITmpWardStaffOption): string {
  const name = [option.firstName, option.lastName].filter(Boolean).join(" ");
  const base = name || `User #${option.cmsUserId}`;
  return option.email ? `${base} (${option.email})` : base;
}

export function TmpWardRuleModal({ rule, onClose }: ITmpWardRuleModalProps) {
  const isEdit = Boolean(rule);
  const { showToast } = useToast();
  const { wards, isLoading: wardsLoading } = useAllWardOptions();
  const { createWardRule, isCreatingWardRule } = useAddTmpWardRule();
  const { updateWardRule, isUpdatingWardRule } = useUpdateTmpWardRule();

  const [prescriberId, setPrescriberId] = useState(rule?.prescriberId ?? "");
  const [hcpHealthProviderId, setHcpHealthProviderId] = useState(
    rule?.hcpHealthProviderId ?? ""
  );
  const [serviceId, setServiceId] = useState(rule?.serviceId ?? "");
  const [wardId, setWardId] = useState(
    rule?.wardId !== undefined ? String(rule.wardId) : ""
  );
  const [actingStaffCmsUserId, setActingStaffCmsUserId] = useState(
    rule?.actingStaffCmsUserId !== undefined
      ? String(rule.actingStaffCmsUserId)
      : ""
  );
  const selectedWardId = Number(wardId) || 0;
  const {
    staff,
    isLoading: staffLoading,
    isError: staffError,
  } = useTmpWardStaff(selectedWardId);

  // The picker follows the ward: once the new ward's staff has loaded, a
  // selection that is not in it is cleared — except the rule's own current
  // user on edit, which stays selectable so the form still submits.
  useEffect(() => {
    if (!selectedWardId || staffLoading || !actingStaffCmsUserId) return;
    const inList = staff.some(
      (option) => String(option.cmsUserId) === actingStaffCmsUserId
    );
    const isRulesCurrentUser =
      rule !== undefined &&
      String(rule.actingStaffCmsUserId) === actingStaffCmsUserId;
    if (!inList && !isRulesCurrentUser) setActingStaffCmsUserId("");
    // Runs when the ward or its staff list changes — not on every keystroke
    // of the selection itself, which would fight the user's own pick.
  }, [selectedWardId, staff, staffLoading]);

  const currentUserMissingFromList =
    rule !== undefined &&
    selectedWardId > 0 &&
    !staffLoading &&
    !staff.some((option) => option.cmsUserId === rule.actingStaffCmsUserId);
  /** The API's own rejection of the staff user (400, ActingStaffCmsUserId). */
  const [actingStaffServerError, setActingStaffServerError] = useState<
    string | undefined
  >();
  /** The API's duplicate-pair rejection (400, HcpHealthProviderId). */
  const [hcpServerError, setHcpServerError] = useState<string | undefined>();
  const [active, setActive] = useState(rule?.active ?? true);
  const [description, setDescription] = useState(rule?.description ?? "");
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const isSaving = isCreatingWardRule || isUpdatingWardRule;

  const touch = (field: string) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const errors = {
    hcpHealthProviderId: hcpHealthProviderId.trim()
      ? undefined
      : "Responsible HCP id is required.",
    wardId: (() => {
      if (!wardId) return "Select a ward.";
      if (!Number.isInteger(Number(wardId)) || Number(wardId) <= 0) {
        return "Ward ID must be a positive number.";
      }
      return undefined;
    })(),
    actingStaffCmsUserId: (() => {
      if (!actingStaffCmsUserId.trim()) return "Select the acting staff user.";
      const parsed = Number(actingStaffCmsUserId);
      if (!Number.isInteger(parsed) || parsed <= 0) {
        return "Select a valid acting staff user.";
      }
      return undefined;
    })(),
  };
  const hasErrors = Object.values(errors).some(Boolean);
  const shownError = (field: keyof typeof errors) =>
    isEdit || touched[field] ? errors[field] : undefined;
  const hcpShownError = hcpServerError ?? shownError("hcpHealthProviderId");

  const handleSave = async () => {
    if (hasErrors) return;

    // An emptied optional field is sent as null (not left out) so an edit
    // clears the stored value instead of silently keeping it.
    const request = {
      hcpHealthProviderId: hcpHealthProviderId.trim(),
      prescriberId: prescriberId.trim() || null,
      serviceId: serviceId.trim() || null,
      wardId: Number(wardId),
      actingStaffCmsUserId: Number(actingStaffCmsUserId),
      active,
      description: description.trim() || null,
    };

    setActingStaffServerError(undefined);
    setHcpServerError(undefined);
    try {
      if (rule) {
        await updateWardRule({ id: rule.id, request });
        showToast("The ward rule was updated successfully.", "success");
      } else {
        await createWardRule(request);
        showToast("The ward rule was created successfully.", "success");
      }
      onClose();
    } catch (error) {
      // A rejected field belongs on that field, not in a toast: the operator
      // fixes the value and saves again.
      const fieldError = (property: string) =>
        error instanceof HttpError
          ? error.errors.find(
              (e) => e.propertyName?.toLowerCase() === property.toLowerCase()
            )
          : undefined;
      const staffUserError = fieldError("ActingStaffCmsUserId");
      const hcpError = fieldError("HcpHealthProviderId");
      if (staffUserError || hcpError) {
        if (staffUserError) setActingStaffServerError(staffUserError.message);
        if (hcpError) setHcpServerError(hcpError.message);
        return;
      }
      showToast(
        errorToastMessage(
          error,
          isEdit
            ? "There was an error updating this ward rule."
            : "There was an error creating this ward rule."
        ),
        "warning"
      );
    }
  };

  return (
    <ModalContainer data-testid="tmp-ward-rule-modal">
      <ModalTitle>{isEdit ? "Edit ward rule" : "New ward rule"}</ModalTitle>

      <ModalForm>
        <FilterGroup>
          <FilterLabel htmlFor="tmp-ward-rule-hcp">
            Responsible HCP id (RIZIV)
          </FilterLabel>
          <FilterInput
            id="tmp-ward-rule-hcp"
            type="text"
            value={hcpHealthProviderId}
            onBlur={() => touch("hcpHealthProviderId")}
            onChange={(e) => {
              touch("hcpHealthProviderId");
              setHcpServerError(undefined);
              setHcpHealthProviderId(e.target.value);
            }}
          />
          <FieldHint>
            The responsibleHcp.healthProviderId TMP sends on the order. Orders
            without it are refused.
          </FieldHint>
          {hcpShownError && (
            <FieldError data-testid="tmp-ward-rule-hcp-error">
              {hcpShownError}
            </FieldError>
          )}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="tmp-ward-rule-prescriber">
            Prescriber id (optional)
          </FilterLabel>
          <FilterInput
            id="tmp-ward-rule-prescriber"
            type="text"
            value={prescriberId}
            onChange={(e) => setPrescriberId(e.target.value)}
          />
          <FieldHint>
            TMP&apos;s prescriber id, if known. Informational only.
          </FieldHint>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="tmp-ward-rule-service">
            Service ID (optional)
          </FilterLabel>
          <FilterInput
            id="tmp-ward-rule-service"
            type="text"
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
          />
          <FieldHint>
            Leave empty to apply the rule to every service of this prescriber.
          </FieldHint>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="tmp-ward-rule-ward">Ward</FilterLabel>
          <AdminSelectField
            id="tmp-ward-rule-ward"
            value={wardId}
            disabled={wardsLoading}
            onBlur={() => touch("wardId")}
            onChange={(e) => {
              touch("wardId");
              setWardId(e.target.value);
            }}
          >
            <option value="">
              {wardsLoading ? "Loading..." : "Select an option"}
            </option>
            {wards.map((ward) => (
              <option key={ward.id} value={ward.id}>
                {ward.id} - {ward.name}
              </option>
            ))}
          </AdminSelectField>
          {shownError("wardId") && (
            <FieldError>{shownError("wardId")}</FieldError>
          )}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="tmp-ward-rule-acting-staff">
            Acting staff user
          </FilterLabel>
          <AdminSelectField
            id="tmp-ward-rule-acting-staff"
            value={actingStaffCmsUserId}
            disabled={!selectedWardId || staffLoading}
            onBlur={() => touch("actingStaffCmsUserId")}
            onChange={(e) => {
              touch("actingStaffCmsUserId");
              setActingStaffServerError(undefined);
              setActingStaffCmsUserId(e.target.value);
            }}
          >
            <option value="">
              {(() => {
                if (!selectedWardId) return "Select a ward first";
                if (staffLoading) return "Loading...";
                return "Select an option";
              })()}
            </option>
            {currentUserMissingFromList && rule && (
              <option value={String(rule.actingStaffCmsUserId)}>
                Current user #{rule.actingStaffCmsUserId} (not in this ward)
              </option>
            )}
            {staff.map((option) => (
              <option key={option.cmsUserId} value={String(option.cmsUserId)}>
                {staffOptionLabel(option)}
              </option>
            ))}
          </AdminSelectField>
          <FieldHint>
            Staff account that archives the patient when TMP stops or cancels an
            order.
          </FieldHint>
          {staffError && (
            <FieldError>Failed to load this ward&apos;s staff.</FieldError>
          )}
          {(actingStaffServerError || shownError("actingStaffCmsUserId")) && (
            <FieldError data-testid="tmp-ward-rule-acting-staff-error">
              {actingStaffServerError || shownError("actingStaffCmsUserId")}
            </FieldError>
          )}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="tmp-ward-rule-description">
            Description (optional)
          </FilterLabel>
          <FilterInput
            id="tmp-ward-rule-description"
            type="text"
            maxLength={255}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Status</FilterLabel>
          <CheckboxField>
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            Active
          </CheckboxField>
          <FieldHint>
            Inactive rules are ignored by the provisioner but kept for
            reference.
          </FieldHint>
        </FilterGroup>

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isSaving}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isSaving || hasErrors}
            onClick={handleSave}
            data-testid="tmp-ward-rule-save"
          >
            {(() => {
              if (isSaving) return isEdit ? "Saving..." : "Creating...";
              return isEdit ? "Save" : "Create";
            })()}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
